/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { FieldSchema, FormField, FormSchema, Option } from "@/models/form";
import useFormHelpers from "@/use/formHelpers";
import { ProductIds } from "@/config/constants";
import {
  NationalIdNumberTypeDefinitions,
  NationalIdNumberTypeIds,
} from "@/models/nationalIdNumberTypes";
import type { Schema } from "yup";

const { calculateAge } = useFormHelpers();

export function validateStringInput(value?: string | undefined | null | unknown): boolean {
  return (
    value !== null && value !== undefined && typeof value === "string" && value.trim().length > 0
  );
}

export function allKeysHaveValues<T extends object>(obj: T): boolean {
  return Object.keys(obj).every(key => {
    // Type assertion to ensure 'key' is a valid key of 'obj'
    const typedKey = key as keyof T;
    return obj[typedKey] !== null && obj[typedKey] !== undefined;
  });
}

export default function useValidation() {
  const validItinRegex = /^(9\d{2})([ -]?)(5\d|6[0-5]|7\d|8[0-8]|9[0-2]|9[4-9])([ -]?)(\d{4})$/;
  const validSSnRegex = /^\d{3}-?\d{2}-?\d{4}$/;
  const validDOBRegex = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/gm;
  const validZipFormat1Regex = /^[0-9]{5}(?:-[0-9]{4})?$/; // Handles 12345, 12345-6789
  const validZipFormat2Regex = /^[0-9]{9}?$/; // Handles 123456789

  function getErrorMessage(field: FormField | FieldSchema) {
    if (field?.errorMessage) {
      return field.errorMessage;
    }

    switch (field?.type) {
      case "email":
        return "Please provide a valid email address.";
      case "compare":
        return `Compared values do not match.`;
      case "phone":
        return "Please provide a valid phone number.";
      case "ssn":
        return "Please provide a valid SSN.";
      case "money":
        return "Please provide a valid amount.";
      default:
        return field?.label ? `${field.label} is required.` : "Please provide a value.";
    }
  }
  function getNationalIdNumberErrorMessage(nationalIdNumberTypeId: number): string {
    return NationalIdNumberTypeDefinitions[nationalIdNumberTypeId]?.errorMessage;
  }
  function addErrorMessage(field: FormField | FieldSchema, message: string) {
    // only add if the message doesn't already exist to avoid duplicates
    if (field.errors && !field.errors.includes(message)) {
      field.errors.push(message);
    }
  }

  async function validate(schema: FormField | FieldSchema, value: any, compare: any = undefined) {
    schema.touched = true;
    // Reset the error messages
    schema.errors = [];

    // If schema has custom validation, let them, handle it
    if (schema.validate) {
      schema.validate(schema, value, compare);
    } else {
      if (schema.required) {
        const trimmedValue = typeof value === "string" ? value.replace(/\s/g, "") : value;
        // Check if the field is requried first
        schema.valid = trimmedValue !== undefined && trimmedValue !== "" && trimmedValue !== null;
      }

      if (
        value === undefined ||
        value === "" ||
        value === null ||
        value === 0 ||
        !schema.required
      ) {
        // If empty we tell it to return the opposite of what is allowed
        schema.valid = !schema.required;
      } else {
        if (schema.type === "email") {
          // Check if the field needs email validation
          const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          schema.valid = re.test(value);
        } else if (schema.type === "date") {
          if (schema.subtype === "dob") {
            // Only allow ages between 0 and 150
            if (calculateAge(value) <= 0 || calculateAge(value) > 150) {
              schema.errors.push("Please provide a valid Date of Birth");
            }
          }
        } else if (schema.type === "phone") {
          // Check if the field needs phone number validation
          const re = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
          schema.valid = re.test(value);
        } else if (schema.type === "text") {
          // Does not allow empty input
          const cleanedInput = value?.trim();
          // Does not allow for urls into any input field
          const urlCheck = /^https?:\/\/.*$/.test(value);
          if (urlCheck === true) {
            schema.errors.push("Must not contain url.");
          }

          if (
            schema.name === "firstName" ||
            schema.name === "middleName" ||
            schema.name === "lastName"
          ) {
            // Only allow standard A-Z a-z values, single spaces and - '
            const specialCharacterCheck = /[^a-zA-Z\s'-]/.test(value);
            if (specialCharacterCheck === true) {
              schema.errors.push(
                `${schema.label} can only contain letters, apostrophes, hyphens, and spaces.`,
              );
            }

            // Passed the validation rules
            schema.valid = schema.errors.length <= 0;
          }

          if (schema.subtype === "address") {
            await validateAddressFields(schema, value);
          }

          schema.valid = cleanedInput?.length >= 1 && schema.errors.length <= 0;
        } else if (schema.type === "textarea") {
          const cleanedText = value.trim();
          if (cleanedText === undefined || cleanedText.length < 5 || cleanedText.length > 50) {
            schema.errors.push("Must be between 5 and 50 characters.");
          } else {
            schema.errors = [];
          }
          schema.valid = schema.errors.length <= 0;
        } else if (schema.type === "password") {
          // Test each rule individually for better readability

          // Adding the undefined checks because for some reason not all
          // rules would get hit if value was undefined...

          // At least 8 characters long and no more than 32 characters
          if (value === undefined || value.length < 8 || value.length > 32) {
            schema.errors.push("Must be between 8 and 32 characters.");
          }

          // Uppercase Alphabet.
          if (value === undefined || /[A-Z]/.test(value) === false) {
            schema.errors.push("Must contain an uppercase letter.");
          }

          // Lowercase Alphabet.
          if (value === undefined || /[a-z]/.test(value) === false) {
            schema.errors.push("Must contain a lowercase letter.");
          }

          // At least one number.
          if (value === undefined || /[0-9]/.test(value) === false) {
            schema.errors.push("Must contain a number.");
          }

          //Special Character.
          if (value === undefined || !new RegExp("[$@$!%*#?&]").test(value)) {
            schema.errors.push("Must contain a special character.");
          }

          // Passed all 5 password rules
          schema.valid = schema.errors.length <= 0;
        } else if (schema.type === "compare") {
          schema.valid =
            compare !== undefined && compare !== "" && compare !== null && value === compare;
        } else if (schema.type === "checkbox") {
          schema.valid = value === true;
        } else if (schema.type === "ssn") {
          const cleanedSsn = `${value}`.split("-").join("");
          const validDigits = /^\d{9}$/.test(cleanedSsn);
          schema.valid = validDigits && cleanedSsn.length === 9;
        } else if (schema.type === "money") {
          schema.valid = value !== undefined;

          if (schema?.subtype === "drawAmount") {
						const minimumDisplay = compare?.minimumDisplay ? compare?.minimumDisplay : `$${compare?.minimum}`;

            const minimumNotMetError = `The entered requested amount does not meet the minimum required amount of ${minimumDisplay}. Please enter an amount greater than or equal to ${minimumDisplay} to proceed.`;

            // 1. Validate that the amount is a number
            if (value === undefined || value === "") {
              addErrorMessage(schema, "Amount is required.");
            }

            // 2. Validate that the amount is a whole number
            if (value === undefined || value % 1 !== 0) {
              schema.errors.push(
                "Only whole dollar values can be entered for the Requested Amount.",
              );
            }

            // 3. Validate that value meets minimum
            // 4. Validate that requested amount is greater than 0
            if (compare.minimum && Number(value) < compare.minimum) {
              schema.errors.push(minimumNotMetError);
            }

            // 5. Validate the requested amount is below available to borrow amount
            if (compare.availableToBorrow && value > compare.availableToBorrow) {
              schema.errors.push(
                "The entered requested amount exceeds the available balance on this loan. Please enter a lower amount to proceed.",
              );
            }

            schema.valid = schema.errors.length <= 0;
          } else if (schema?.subtype === "initialDrawAmount") {
            validateBorrowAmount(schema, value, compare);
          } else {
            // Check if the value is allowed to be 0
            if (
              (schema.allowZero === false && typeof value === "string" && value.startsWith("0")) ||
              (schema.allowZero === false && typeof value === "number" && value === 0)
            ) {
              addErrorMessage(schema, "Amount must be greater than 0.");
            }

            if (value === undefined) {
              addErrorMessage(schema, "Amount is required");
            }
          }

          // Passed all rules
          schema.valid = schema.errors.length <= 0;
        } else if (schema.type === "zipcode") {
          // Handles 12345, 12345-6789
          const formatCheck1 = /^[0-9]{5}(?:-[0-9]{4})?$/.test(value);
          // Handles 123456789
          const formatCheck2 = /^[0-9]{9}?$/.test(value);

          if (value === undefined) {
            schema.errors.push("Invalid zip code");
          } else if (value.length === 5 && formatCheck1) {
            schema.valid = true;
          } else if (value.length === 10 && formatCheck1) {
            schema.valid = true;
          } else if (value.length === 9 && formatCheck2) {
            schema.valid = true;
          } else {
            schema.errors.push("Invalid zip code");
          }

          // Passed all checks
          schema.valid = schema.errors.length <= 0;
        }
      }

      if (schema.valid === false && schema.errors.length <= 0) {
        schema.errors = [getErrorMessage(schema)];
      }
    }
  }

  function validateForm(schema: FormSchema, formValue: any): boolean {
    let valid = true;

    for (const [key, value] of Object.entries(schema)) {
      const keySchema = value as any;
      if (keySchema.type === "autocomplete") {
        const compareValue = formValue[key] || {};
        validate(value as FormField, compareValue[value.autocompleteProperty || ""] || "");
      } else if (keySchema.validate) {
        keySchema.validate(value as FormField, formValue[key], formValue[keySchema.compareField]);
      } else {
        validate(value as FormField, formValue[key], formValue[keySchema.compareField]);
      }
      valid = valid && (value as any).valid;
    }

    return valid;
  }

  async function validateAddressFields(schema: FormField | FieldSchema, value: any) {
    schema.touched = true;
    // Reset the error messages
    schema.errors = [];

    // character limits: handled by maxlength input property

    if (schema.name === "addrLine1") {
      const errorMessage = `${schema.label} can only contain letters, numbers, apostrophes, hyphens, and spaces.`;

      // Only allow standard A-Z a-z 0-9 values, single space, and - '
      const specialCharacterCheck = /[^A-Za-z0-9-' ]/.test(value);
      if (specialCharacterCheck === true) {
        schema.errors.push(errorMessage);
      }
    }

    if (schema.name === "city") {
      const errorMessage = `${schema.label} can only contain letters, apostrophes, hyphens, and spaces.`;
      // Only allow standard A-Z a-z values, and single spaces
      const invalidCharCheck = /[^A-Za-z-' ]/.test(value);
      if (invalidCharCheck === true) {
        schema.errors.push(errorMessage);
      }
    }

    schema.valid = schema.errors.length <= 0;
  }

  async function validateZipcode(schema: FormField | FieldSchema, value: any) {
    schema.touched = true;
    // Reset the error messages
    schema.errors = [];
    // Handles 12345, 12345-6789
    const formatCheck1 = /^[0-9]{5}(?:-[0-9]{4})?$/.test(value);
    // Handles 123456789
    const formatCheck2 = /^[0-9]{9}?$/.test(value);

    if (value === undefined) {
      schema.errors.push("Invalid zip code");
    } else if (value.length === 5 && formatCheck1) {
      schema.valid = true;
    } else if (value.length === 10 && formatCheck1) {
      schema.valid = true;
    } else if (value.length === 9 && formatCheck2) {
      schema.valid = true;
    } else {
      schema.errors.push("Invalid zip code");
    }

    // Passed all checks
    schema.valid = schema.errors.length <= 0;
  }

  function validateAddressLine2Fields(
    unitType: FieldSchema,
    unitNumber: FieldSchema,
    unitTypeValue: any,
    unitNumberValue: any,
  ): boolean {
    let valid = true;

    if (unitTypeValue && unitNumberValue) {
      unitType.required = true;
      unitNumber.required = true;
      valid = true;
    } else if (unitTypeValue && !unitNumberValue) {
      unitType.required = true;
      unitNumber.required = true;
      valid = false;
    } else if (!unitTypeValue && unitNumberValue) {
      unitType.required = true;
      unitNumber.required = true;
      valid = false;
    } else if (!unitTypeValue && !unitNumberValue) {
      unitType.required = false;
      unitNumber.required = false;
      unitType.valid = true;
      unitNumber.valid = true;
      unitType.errors = [];
      unitNumber.errors = [];
      valid = true;
    }

    return valid;
  }

  async function validateBorrowAmount(schema: any, value: any, compare: any) {
    schema.errors = [];
    let errorMessage = "";

    if (value !== null) {
      const amount =
        (typeof value === "string" && value !== undefined) || value !== ""
          ? parseInt(value)
          : value;

      if (schema?.productTypeId === ProductIds.PEL) {
        schema.valid = amount <= compare && amount >= schema.minimum;
      } else {
        schema.valid =
          amount <= compare &&
          ((schema.allowZero === true && amount === 0) ||
            (schema.allowZero === false && amount > 0) ||
            amount >= 1000);
      }

      if (schema.valid === false) {
        if (amount > compare) {
          errorMessage =
            schema?.productTypeId === ProductIds.LOC
              ? "The amount requested is more than the total line of credit you've been approved for."
              : "The amount requested is more than your approved maximum loan amount.";
          schema.errors = [errorMessage];
        } else {
          errorMessage =
            schema?.productTypeId === ProductIds.LOC
              ? "Initial requests for funds must be at least $1,000. You may also enter $0 if you are unsure."
              : schema.errorMessage || "";
          schema.errors = [errorMessage];
        }
      }
    }
  }
  function resetFieldValidation(schema: FieldSchema | FormSchema) {
    schema.touched = false;
    schema.valid = undefined;
    schema.errors = [];
  }

  function getOption(options: Option[], value?: number | string) {
    return options.find((o: Option) => o.value === Number(value));
  }

  function getOptionLabel(options: Option[], value?: number | string) {
    return getOption(options, value)?.label;
  }

  function isNationalIdNumberMasked(nationalIdNumber: string) {
    const masked = /^X{3}-X{2}-\d{4}$/.test(nationalIdNumber) || /^\*{3}-\*{2}-\d{4}$/.test(nationalIdNumber) || /^\*{5}\d{4}$/.test(nationalIdNumber);
    return masked;
  }

  async function validateSingleField(schema: Schema, fieldSchema: FieldSchema, value: any) {
    try {
      const res = await schema.validateAt(fieldSchema.name || "", value, { abortEarly: false });
      return { valid: true, errors: undefined, results: res };
    } catch (err: any) {
      // Use err.inner to access all individual errors
      const formattedErrors = err.inner.reduce((acc: any, error: any) => {
        acc[error.path] = error.message;
        return acc;
      }, {});
      console.log(formattedErrors);
      return { valid: false, errors: formattedErrors, results: err };
    }
  }

  async function validateInputs(schema: Schema, values: any) {
    try {
      const res = await schema.validate(values, { abortEarly: false });
      return { valid: true, errors: undefined, results: res };
    } catch (err: any) {
      // Use err.inner to access all individual errors
      const formattedErrors = err.inner.reduce((acc: any, error: any) => {
        acc[error.path] = error.message;
        return acc;
      }, {});
      console.log(formattedErrors);
      return { valid: false, errors: formattedErrors, results: err };
    }
  }
  return {
    getErrorMessage,
    getNationalIdNumberErrorMessage,
    getOption,
    getOptionLabel,
    isNationalIdNumberMasked,
    resetFieldValidation,
    validate,
    validateForm,
    validateAddressLine2Fields,
    validateBorrowAmount,
    validateSingleField,
    validateInputs,
    validItinRegex,
    validSSnRegex,
    validDOBRegex,
    validZipFormat1Regex,
    validZipFormat2Regex,
  };
}
